import { Trans, useTranslation }         from 'react-i18next'
import {
  Autocomplete,
  Box, Button,
  Collapse,
  Container,
  Grid,
  IconButton,
  Link,
  Stack, TextField,
  Typography
}                                        from '@mui/material'
import BaseTable                         from '../ui/components/base-table'
import { useApi }                        from '../utils/api'
import React, { useEffect, useState }    from 'react'
import { fDateTime, fPrice }             from '../utils/textutils'
import Page                              from '../ui/components/Page'
import { BaseBreadcrumbs }               from '../ui/components/BaseBreadcrumbs'
import { Link as RouterLink, useParams } from 'react-router-dom'
import palette                           from '../ui/theme/palette'
import Iconify                           from '../ui/components/Iconify'
import ShortFormControl                  from '../ui/components/short-form-control'
import { DateTime }                      from 'luxon'
import Label                             from '../ui/components/Label'

export default function TransactionsPage () {

  const { t } = useTranslation()
  const api = useApi()
  const [filterOpen, setFilterOpen] = useState(true)
  const { query, from, to, serviceIds, } = useParams()
  const [filter, setFilter] = useState({
    dateFrom: from ?? DateTime.now().toFormat('yyyy-MM-dd'),
    dateTo: to ?? DateTime.now().toFormat('yyyy-MM-dd'),
    query: query ?? '',
    selectedServices: [],
  })
  const [shownFilter, setShownFilter] = useState({
    dateFrom: from ?? DateTime.now().toFormat('yyyy-MM-dd'),
    dateTo: to ?? DateTime.now().toFormat('yyyy-MM-dd'),
    query: query ?? '',
    selectedServices: [],
  })
  const [services, setServices] = useState([])
  const [baseTableQuery, setBaseTableQuery] = useState('{}')

  useEffect(() => {
    api.service.list().then(({data}) => {
      setServices(data)
      const selectedServices = (serviceIds ?? '').split(',')?.map(it => parseInt(it))?.map(serviceId => data.find(it => it.id === serviceId))?.filter(it => !!it) ?? []
      setFilter((prev) => ({
        ...prev,
        selectedServices: selectedServices
      }))
      setShownFilter((prev) => ({
        ...prev,
        selectedServices: selectedServices
      }))
    }).catch(e => {})
  }, [])

  const onSearchSubmit = (e) => {
    e.preventDefault()
    setBaseTableQuery(JSON.stringify(filter))
    setShownFilter(filter)
  }

  const makeFilterTitle = () => {
    const reqDateFrom = (shownFilter.dateFrom ? DateTime.fromFormat(shownFilter.dateFrom, 'yyyy-MM-dd') : DateTime.now()).toFormat('dd.MM.yyyy')
    const reqDateTo = (shownFilter.dateTo ? DateTime.fromFormat(shownFilter.dateTo, 'yyyy-MM-dd') : DateTime.now()).toFormat('dd.MM.yyyy')
    const reqServices = shownFilter.selectedServices.length === 0 ? t('all services') : shownFilter.selectedServices.map(it => it.title).join(', ')
    const reqQuery = shownFilter.query ? shownFilter.query : "''"
    return <Trans i18nKey={'transactionsFilterHighlight'} values={{ from: reqDateFrom, to: reqDateTo, services: reqServices, query: reqQuery }} />
  }

  const columns = [
    { field: 'id', headerName: t('ID'), width: 80, sortable: false, filterable: false, renderCell: (cell) => {
    return <Link
      component={RouterLink}
      variant="body2"
      to={`/transactions/${cell.row.id}`}
    >
      {cell.row.id}
    </Link>
  }  },
    { field: 'created', headerName: t('Created'), width: 140, sortable: false, filterable: false, renderCell: (cell) => {
        return fDateTime(cell.row.created)
    }},
    { field: 'telNumber', headerName: t('Tel. number'), width: 140, sortable: false, filterable: false },
    { field: 'service', headerName: t('Service'), width: 140, sortable: false, filterable: false, renderCell: (cell) => {
      return cell.row?.service?.title ?? '---'
      } },
    { field: 'message.content', headerName: t('Content'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return (
          <Link
            component={RouterLink}
            variant="body2"
            to={`/messages/${cell.row.message?.id}`}
          >
            {cell.row.message?.content}
          </Link>
        )
    }},
    { field: 'message', headerName: t('Delivery status'), width: 120, sortable: false, filterable: false, renderCell: (cell) => {
        const icon = cell.row.message?.deliveryStatus === 'delivered' ? 'material-symbols:bookmark-check' : (cell.row.message?.deliveryStatus === 'sent' ? 'material-symbols:question-exchange' : 'material-symbols:exclamation')
        const color = cell.row.message?.deliveryStatus === 'delivered' ? 'success' : (cell.row.message?.deliveryStatus === 'sent' ? 'secondary' : 'warning')
        return <Label color={color} startIcon={<Iconify icon={icon}/>}>{t(`deliverystatus-${cell.row.message?.deliveryStatus}`)}</Label>
      }},
    { field: 'priceWithVat', headerName: t('Price'), width: 100, sortable: false, filterable: false, renderCell: (cell) => fPrice(cell.row.priceWithVat) },
  ]

  useEffect(() => {
    setBaseTableQuery(JSON.stringify(filter))
  }, [])

  return (<Page title={t('Transactions')}>
    <Container>
      <BaseBreadcrumbs key={'breadcrumb-transactions'}
                       title={t('Transactions')}
                       items={[]}/>
      <Grid container>
        <Grid item xs={12}>
          <Stack direction={'row'} spacing={2} alignItems={'baseline'} mb={2}>
            <Typography variant={'h6'}>{t('Transactions')}</Typography>
            <Typography variant="body1" sx={{ color: palette.text.disabled }}>
              {makeFilterTitle()}
            </Typography>
            <Box flexGrow={1}/>
            <IconButton onClick={(e) => setFilterOpen(prev => !prev)}>
              <Iconify icon={filterOpen ? 'material-symbols:filter-alt-outline' : 'material-symbols:filter-alt'}/>
            </IconButton>
          </Stack>
        </Grid>
      </Grid>
      <Collapse in={filterOpen}>
        <Grid container component={'form'} onSubmit={onSearchSubmit} spacing={2} rowSpacing={2} direction={'row'} alignItems={'flex-end'} justifyContent={'flex-end'} mb={2}>
          <Grid item xs={6} sm={'auto'}>
            <ShortFormControl title={t('From')} handleChange={(name) => (e) => { setFilter((prev) => ({
              ...prev,
              dateFrom: e.target?.value
            })) }} value={filter.dateFrom} name={'from'} inputProps={{ type: 'date' }} />
          </Grid>
          <Grid item xs={6} sm={'auto'}>
            <ShortFormControl title={t('To')} handleChange={(name) => (e) => { setFilter((prev) => ({
              ...prev,
              dateTo: e.target?.value
            })) }} value={filter.dateTo} name={'to'} inputProps={{ type: 'date' }} />
          </Grid>
          {services?.length && <Grid item xs={12} sm={'auto'} sx={{ minWidth: { sm: '200px' } }}>
            <Autocomplete
              multiple
              fullWidth
              id="tags-services"
              options={services}
              getOptionLabel={(option) => option.title}
              value={filter.selectedServices}
              onChange={(e, newValue) => {
                setFilter((prev) => ({
                  ...prev,
                  selectedServices: newValue
                }))
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label={t('Services')}
                />
              )}
            />
          </Grid>}
          <Grid item xs={12} sm={'auto'} sx={{ minWidth: { sm: '200px' } }}>
            <ShortFormControl title={t('Search...')} handleChange={(name) => (e) => { setFilter((prev) => ({
              ...prev,
              query: e.target?.value
            })) }} value={filter.query} name={'query'} inputProps={{ type: 'text' }} />
          </Grid>
          <Grid item xs={12} sx={{ maxWidth: { sm: '200px' } }}>
            <Button fullWidth type={'submit'} variant="outlined">{t('SearchAction')}</Button>
          </Grid>
        </Grid>
      </Collapse>
      <BaseTable
        columns={columns}
        query={baseTableQuery}
        loadCallback={async (query, page, pageSize) => {
          const queryObject = JSON.parse(query)
          const dateFrom = queryObject.dateFrom?.replaceAll('-','')
          const dateTo = queryObject.dateTo?.replaceAll('-','')
          const serviceIds = queryObject.selectedServices?.map(it => it.id) ?? []
          try {
            return await api.transaction.list(dateFrom, dateTo, serviceIds, queryObject.query, page, pageSize)
          } catch (e) {
            console.log('Failed to load transactions', e)
            return {
              data: [],
              totalCount: 0,
            }
          }
        }}
      />
    </Container>
  </Page>)
}